import { useEffect, useState } from 'react'
import MFAccordion from '../../components/ui-components/MFAccordion'
import { EventNames } from '../../types/misc.types'
import { disableHardwareBack, lightImpactVibrate, postCTA, trackEvent, updateAppStatusBar } from '../../utils/appBridge'
import { RebalanceInstructions } from '../../components/power-rebalancing/RebalanceInstructions'
import ReasonsToRebalance from '../../components/power-rebalancing/ReasonsToRebalance'
import { RebalanceHeader } from '../../components/power-rebalancing/RebalanceHeader'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { powerRebalanceISIN } from '../../repository/rebalance'
import { useNavigate, useParams } from 'react-router-dom'
import { MutualFundListItem } from '../../components/power-rebalancing/MutualFundList'
import { roundOffAndFormatNumberWithCurrency, isWithinDays } from '../../utils/utils'
import BackButton from '../../components/ui-components/BackButton'
import ExitLoadAndTax from '../../components/power-rebalancing/tax/ExitLoadAndTax'
import { getMutualFundDetailCTA } from '../../utils/mutualFundDetail'
import { FAQRebalancing } from '../../components/power-rebalancing/FAQRebalancing'
import PeerAnalyserError from '../../components/error-states/PeerAnalyserError'
import TransferRiveComponent from '../../components/Questionnaire/TransferRiveComponent'
import { FundRebalanceData, RebalanceType } from '../../types/rebalance'
import { RankExplanationBottomSheet } from '../../components/power-rebalancing/RankExplanationBottomSheet'
import { WhySwitchOnTrackBottomSheet } from '../../components/power-rebalancing/WhySwitchOnTrackBottomSheet'
import { GrowthPlanBottomSheet } from '../../components/power-rebalancing/GrowthPlanBottomSheet'
import { ElssAlternativesBottomSheet } from '../../components/power-rebalancing/ElssAlternativesBottomSheet'
import { PowerPerformanceForms } from '../../types/mutual-fund-detail.types'
import { useBottomSheet } from '../../state-management/contexts/BottomSheetContext'
import { getTopPadding } from '../../utils/sessionStorage'
import Info from '../../components/ui-components/Info'
import RenderHTML from '../../components/ui-components/RenderHTML'
import { useRebalancePlanStore } from '../../state-management/rebalancePlanStore'
import RebalancingDisclaimer from '../../components/power-rebalancing/RebalancingDisclaimer'

/**
 * Checks if less than 30 days have passed since the recommendation date
 * @param recommendationDate - The recommendation date as epoch timestamp
 * @returns true if less than 30 days have passed, false otherwise
 */
const isWithin60Days = (recommendationDate: number | undefined): boolean => {
  return isWithinDays(recommendationDate, 60)
}

const generateTransferTextBasedOnEnum = (
  rebalanceType: RebalanceType,
  highExposureTransferAmount: number,
  missedGains: number
) => {
  switch (rebalanceType) {
    case RebalanceType.SWP:
    case RebalanceType.SWP_ELSS:
      return 'Transfer 100% as SWP'
    case RebalanceType.LUMPSUM:
    case RebalanceType.LUMPSUM_ELSS:
      return 'Transfer 100% Lumpsum'
    case RebalanceType.LUMPSUM_SIP:
    case RebalanceType.LUMPSUM_SIP_ELSS:
      return missedGains > 0 ? 'Transfer 100% Lumpsum + SIP' : 'Transfer 100% Lumpsum'
    case RebalanceType.SWP_SIP:
    case RebalanceType.SWP_SIP_ELSS:
      return 'Transfer 100% as SWP + SIP'
    case RebalanceType.LUMPSUM_HIGH_EXPOSURE:
    case RebalanceType.LUMPSUM_HIGH_EXPOSURE_ELSS:
      return `Transfer ${roundOffAndFormatNumberWithCurrency(highExposureTransferAmount)} Lumpsum`
    case RebalanceType.SWP_HIGH_EXPOSURE:
    case RebalanceType.SWP_HIGH_EXPOSURE_ELSS:
      return `Transfer ${roundOffAndFormatNumberWithCurrency(highExposureTransferAmount)} as SWP`
    case RebalanceType.SIP:
    case RebalanceType.SIP_ELSS:
      return 'Transfer only SIP'
  }
}

const CurrentToRebalanceFund = (fundRebalanceData: FundRebalanceData) => {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const {
    isin,
    name,
    fundNature,
    fundCategory,
    purchaseMode,
    powerRank,
    performanceRating,
    missedGains,
    highExposure,
    mfLogoUrl,
    sipAmount,
    formType,
    investedAmount,
    switchSuggestions,
    rebalanceStrategyType,
    highExposureTransferAmount
  } = fundRebalanceData
  const {
    name: switchFundName,
    isin: switchFundIsin,
    fundNature: switchFundNature,
    fundCategory: switchFundCategory,
    mfLogoUrl: switchFundMfLogoUrl,
    powerRank: switchFundPowerRank,
    performanceRating: switchFundPerformanceRating,
    missedGains: switchFundMissedGains,
    highExposure: switchFundHighExposure,
    sipAmount: switchFundSipAmount,
    isRegular: switchFundIsRegular,
    exclusionReason: switchFundExclusionReason
  } = switchSuggestions?.[0] || []
  return (
    <div className='relative'>
      <div className='mx-5 rounded-xl bg-grey80 pb-[2px] z-10 relative'>
        <div className='text-grey50 text-13 py-1 px-3 font-medium'>Current</div>
        <div className='bg-white mx-[2px] rounded-xl'>
          <MutualFundListItem
            isin={isin}
            name={name}
            fundNature={fundNature}
            fundCategory={fundCategory}
            purchaseMode={purchaseMode}
            powerRank={powerRank}
            performanceRating={performanceRating}
            highExposure={highExposure}
            mfLogoUrl={mfLogoUrl}
            sipAmount={sipAmount}
            isRegular={purchaseMode === 'REGULAR'}
            formType={formType}
            investedAmount={investedAmount}
            onClick={() => {
              postCTA(JSON.stringify(getMutualFundDetailCTA({ isin, navigationStyle: 'PUSH' })))
            }}
            borderRequired={false}
          />
        </div>
      </div>
      <div
        className={`max-w-fit mx-5 h-full relative z-1 ${performanceRating === PowerPerformanceForms.ON_TRACK ? 'max-h-[100px]' : 'max-h-[72px]'}`}
      >
        {performanceRating === PowerPerformanceForms.ON_TRACK && (
          <div
            className='text-13 text-grey40 text-center underline z-10 top-[25%] left-1/2 -translate-x-1/2 -translate-y-1/2 absolute cursor-pointer'
            onClick={() => {
              lightImpactVibrate()
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  eventId: 'WhySwitchOnTrack',
                  responseName: 'Click',
                  screenType: `REBALANCE_PLAN_${fundRebalanceData.isin}`
                }
              })
              openBottomSheet(
                <WhySwitchOnTrackBottomSheet
                  onDismiss={() => {
                    trackEvent({
                      eventName: EventNames.UserClicked,
                      payload: {
                        eventId: 'WhySwitchOnTrackBottomSheet',
                        responseName: 'Close',
                        screenType: `REBALANCE_PLAN_${isin}`
                      }
                    })
                    closeBottomSheet()
                  }}
                  ScreenName={`REBALANCE_PLAN_${isin}`}
                />
              )
            }}
          >
            Why switch On-track Funds?
          </div>
        )}
        <div className='z-10 w-full absolute text-center top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-status-text-in-form text-13 font-medium'>
          {generateTransferTextBasedOnEnum(rebalanceStrategyType, highExposureTransferAmount, missedGains)}
        </div>
        <TransferRiveComponent riveUrl='/rives/risk-profiles/transfer.riv' />
      </div>
      <div className='mx-5 rounded-xl bg-status-in-form pb-[2px] z-10 relative'>
        <div className='text-13 py-1 px-3 text-status-text-in-form font-medium'>Recommended</div>
        <div className='bg-white mx-[2px] rounded-xl'>
          <MutualFundListItem
            isin={switchFundIsin}
            name={switchFundName}
            fundNature={switchFundNature}
            fundCategory={switchFundCategory}
            purchaseMode={purchaseMode}
            powerRank={switchFundPowerRank}
            performanceRating={switchFundPerformanceRating}
            missedGains={switchFundMissedGains}
            highExposure={switchFundHighExposure}
            mfLogoUrl={switchFundMfLogoUrl}
            sipAmount={switchFundSipAmount}
            isRegular={switchFundIsRegular}
            exclusionReason={switchFundExclusionReason}
            formType={formType}
            investedAmount={investedAmount}
            onClick={() => {
              postCTA(JSON.stringify(getMutualFundDetailCTA({ isin: switchFundIsin, navigationStyle: 'PUSH' })))
            }}
            borderRequired={false}
          />
        </div>
      </div>
      {switchFundCategory === 'ELSS_TAX_SAVINGS' && (
        <div className='mx-5'>
          <div className='text-13 text-grey40 border-[#CBF7D7] border-[1px] rounded-xl px-4 py-2 inline-flex gap-3 mt-2 w-full relative'>
            <div>
              <svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' fill='none'>
                <g fill-rule='evenodd' clip-path='url(#a)' clip-rule='evenodd'>
                  <path fill='#CBF7D7' d='M20 28c5.302 0 8-2.698 8-8s-2.698-8-8-8-8 2.698-8 8 2.698 8 8 8Z' />
                  <path
                    fill='#00661D'
                    d='M18.377 22.207a.668.668 0 0 0-.677.66c0 .364.303.66.677.66h3.247a.668.668 0 0 0 .676-.66.668.668 0 0 0-.676-.66h-.947v-3.035a.668.668 0 0 0-.677-.66h-1.082a.668.668 0 0 0-.677.66c0 .364.303.66.677.66h.406v2.375h-.947Zm2.706-5.675A1.07 1.07 0 0 1 20 17.588a1.07 1.07 0 0 1-1.082-1.056A1.07 1.07 0 0 1 20 15.477a1.07 1.07 0 0 1 1.082 1.055Z'
                  />
                </g>
                <defs>
                  <clipPath id='a'>
                    <path fill='#fff' d='M12 12h16v16H12z' />
                  </clipPath>
                </defs>
              </svg>
            </div>
            <div className='self-center mb-[2px]'>
              Want to avoid ELSS category? <br />{' '}
              <span
                className='text-black underline font-medium cursor-pointer'
                onClick={() => {
                  lightImpactVibrate()
                  trackEvent({
                    eventName: EventNames.UserClicked,
                    payload: {
                      eventId: 'ViewAlternatives',
                      responseName: 'Open',
                      screenType: `REBALANCE_PLAN_${isin}`
                    }
                  })
                  openBottomSheet(
                    <ElssAlternativesBottomSheet
                      // alternatives={data?.data?.switchSuggestions}
                      onDismiss={() => {
                        trackEvent({
                          eventName: EventNames.UserClicked,
                          payload: {
                            eventId: 'ViewAlternativesBottomSheet',
                            responseName: 'Close',
                            screenType: `REBALANCE_PLAN_${isin}`
                          }
                        })
                        closeBottomSheet()
                      }}
                    />
                  )
                }}
              >
                View alternatives
              </span>
            </div>
          </div>
        </div>
      )}
      {performanceRating === PowerPerformanceForms.OUT_OF_FORM && highExposure && (
        <div className='mx-5'>
          <div className='text-13 text-grey40 border-[#CBF7D7] border-[1px] rounded-xl px-4 py-2 inline-flex gap-3 mt-2 w-full relative'>
            <div className='self-center'>
              <svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' fill='none'>
                <g fill-rule='evenodd' clip-path='url(#a)' clip-rule='evenodd'>
                  <path fill='#CBF7D7' d='M20 28c5.302 0 8-2.698 8-8s-2.698-8-8-8-8 2.698-8 8 2.698 8 8 8Z' />
                  <path
                    fill='#00661D'
                    d='M18.377 22.207a.668.668 0 0 0-.677.66c0 .364.303.66.677.66h3.247a.668.668 0 0 0 .676-.66.668.668 0 0 0-.676-.66h-.947v-3.035a.668.668 0 0 0-.677-.66h-1.082a.668.668 0 0 0-.677.66c0 .364.303.66.677.66h.406v2.375h-.947Zm2.706-5.675A1.07 1.07 0 0 1 20 17.588a1.07 1.07 0 0 1-1.082-1.056A1.07 1.07 0 0 1 20 15.477a1.07 1.07 0 0 1 1.082 1.055Z'
                  />
                </g>
                <defs>
                  <clipPath id='a'>
                    <path fill='#fff' d='M12 12h16v16H12z' />
                  </clipPath>
                </defs>
              </svg>
            </div>
            <div className='self-center mb-[2px]'>
              Since this fund has high exposure (concentration risk), we recommend reducing it partially. We'll guide you on the remaining
              amount in your next rebalancing plan.
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export const RebalancePlanISINPage = () => {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { isin } = useParams()
  const [isAccordionOpen, setIsAccordionOpen] = useState(false)
  const { isInfoVisible, dismissInfo, planStartDate } = useRebalancePlanStore()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['rebalance-plan-isin', isin],
    queryFn: () => powerRebalanceISIN(isin as string),
    retry: 1
  })

  useEffect(() => {
    disableHardwareBack()
    updateAppStatusBar('FFFFFF', 'dark')
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        screenType: `REBALANCE_PLAN_${isin}`
      }
    })
  }, [])

  const handleBack = () => {
    lightImpactVibrate()
    navigate(-1)
  }

  const handleGrowthPlanClick = () => {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        eventId: 'GrowthPlan',
        responseName: 'Expand',
        screenType: 'REBALANCE_PLAN_PAGE'
      }
    })
    openBottomSheet(
      <GrowthPlanBottomSheet
        onDismiss={() => {
          trackEvent({
            eventName: EventNames.UserClicked,
            payload: {
              eventId: 'GrowthPlanBottomSheet',
              responseName: 'Close',
              screenType: 'REBALANCE_PLAN_PAGE'
            }
          })
          closeBottomSheet()
        }}
        ScreenName={`REBALANCE_PLAN_${isin}`}
      />
    )
  }

  const handleRankExplanationClick = () => {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        eventId: 'RankExplanation',
        responseName: 'Expand',
        screenType: 'REBALANCE_PLAN_PAGE'
      }
    })
    openBottomSheet(
      <RankExplanationBottomSheet
        onDismiss={() => {
          trackEvent({
            eventName: EventNames.UserClicked,
            payload: {
              eventId: 'RankExplanationBottomSheet',
              responseName: 'Close',
              screenType: 'REBALANCE_PLAN_PAGE'
            }
          })
          closeBottomSheet()
        }}
        ScreenName={`REBALANCE_PLAN_${isin}`}
      />
    )
  }

  return (
    <div className={`h-full w-full overflow-hidden ${isLoading || isError ? 'bg-white' : 'bg-grey80'}`}>
      <div
        style={{ height: `calc(${getTopPadding()}px + 54px)` }}
        key='small'
        id='fullHeaderSmall'
        className='w-full flex z-[99] bg-white fixed top-0 left-0 right-0'
      >
        <div
          style={{ paddingTop: `calc(${getTopPadding()}px + 16px)` }}
          className={`flex items-center relative w-full pb-5 pr-5 pl-[19px] h-full bg-white`}
        >
          <div className='flex absolute z-[100]'>
            <BackButton size={24} onClick={handleBack} />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className='p-5'>
          <div className='flex flex-col gap-4'>
            <div className='h-[9px] max-w-[72px] bg-grey80 animate-pulse rounded'></div>
            <div className='w-[200px] h-[15px] bg-grey80 animate-pulse rounded'></div>
          </div>
        </div>
      ) : isError ? (
        <div className='flex items-center h-[calc(100vh-80px)] relative mb-[20px] mx-5'>
          <PeerAnalyserError
            onRetry={() => {
              queryClient.invalidateQueries({ queryKey: ['rebalance-plan-isin', isin] })
            }}
          />
        </div>
      ) : (
        <div
          style={{ paddingTop: `calc(${getTopPadding()}px + 54px)` }}
          className={`h-[calc(100vh-60px)] w-full flex flex-col flex-1 overflow-y-scroll no-scrollbar overflow-x-hidden overscroll-none bg-grey`}
        >
          <div className='bg-white pb-8'>
            {(data?.data?.recommendationStatus === 'UPDATED' || data?.data?.recommendationStatus === 'NEW') &&
              isin &&
              isInfoVisible(isin) &&
              isWithin60Days(planStartDate) && (
                <div className='px-5 pb-4'>
                  <Info
                    className='bg-status-on-track px-4 py-3 rounded-xl relative'
                    lineClassName='bg-status-text-on-track'
                    contentClassName='text-status-text-on-track mr-10'
                  >
                    <div onClick={() => isin && dismissInfo(isin)} className='absolute top-[10px] right-3'>
                      <RenderHTML innerHTMLText={CloseIcon} />
                    </div>
                    <strong className='text-status-text-on-track'>
                      {data?.data?.recommendationStatusReasonHeader ||
                        `This recommendation has been ${data?.data?.recommendationStatus === 'NEW' ? 'added' : 'updated'}  after our latest review`}
                    </strong>
                    <br />
                    {data?.data?.recommendationStatusReason}
                  </Info>
                </div>
              )}
            {data?.data?.recommendationStatus === 'INVALIDATED' && isWithin60Days(planStartDate) && (
              <div className='px-5 pb-4'>
                <Info
                  className='bg-[#FFF4EB] px-4 py-3 rounded-xl'
                  lineClassName='bg-orange10'
                  contentClassName='text-orange10'
                >
                  <strong className='text-orange10'>
                    {data?.data?.recommendationStatusReasonHeader || 'This fund no longer needs rebalancing'}
                  </strong>
                  <br />
                  {data?.data?.recommendationStatusReason}
                </Info>
              </div>
            )}
            <RebalanceHeader
              type={data?.data?.rebalanceStrategyType}
              currentWealth={data?.data?.currentWealth}
              highExposureTransferAmount={data?.data?.highExposureTransferAmount}
              isElss={data?.data?.isElss}
              missedGains={data?.data?.missedGains}
              isHighExposureFund={data?.data?.isHighExposureFund}
              recommendationDate={data?.data?.recommendationDate}
            />
            <CurrentToRebalanceFund {...data?.data} />
            <div className='px-5 border rounded-xl mt-8 border-[#EBF2F2] mx-5'>
              <MFAccordion
                title='Steps to rebalance'
                icon={'/images/steps_to_rebalance_tick.png'}
                middleContentTitleClassName='!text-base !font-normal'
                accordionHeaderClassName='!p-0'
                iconClassName='!w-[22px] !h-[22px]'
                isExpanded={isAccordionOpen}
                isBorderTop={false}
                chevronTopPadding='40%'
                accordionBodyClassName='!p-0'
                onAccordionClick={() => {
                  setIsAccordionOpen((prev) => {
                    trackEvent({
                      eventName: EventNames.UserClicked,
                      payload: {
                        eventId: 'StepsToRebalance',
                        responseName: 'Expand',
                        screenType: 'REBALANCE_PLAN_PAGE'
                      }
                    })
                    return !prev
                  })
                }}
              >
                <RebalanceInstructions
                  type={data?.data?.rebalanceStrategyType}
                  swpAmountPerMonth={data?.data?.stepsToRebalance?.swpAmountPerMonth}
                  swpDurationInMonths={data?.data?.stepsToRebalance?.swpDurationInMonths}
                  newFundName={data?.data?.switchSuggestions?.[0]?.name}
                  currentFundName={data?.data?.name}
                  highExposureTransferAmount={data?.data?.highExposureTransferAmount}
                  currentWealth={data?.data?.currentWealth}
                  isHighExposureFund={data?.data?.isHighExposureFund}
                  missedGains={data?.data?.missedGains}
                  recommendationDate={data?.data?.recommendationDate}
                />
              </MFAccordion>
            </div>
            <div onClick={handleRankExplanationClick} className='text-13 text-grey40 text-center mt-8 underline'>
              Why is Rank 1 not always recommended?
            </div>
            {data?.data?.distributionStatus === 'INCOME' && (
              <div onClick={handleGrowthPlanClick} className='text-13 text-grey40 text-center mt-4 underline'>
                Why we recommend investing in a Growth plan?
              </div>
            )}
          </div>
          <div className='bg-white mt-3'>
            <ReasonsToRebalance
              fundName={data?.data?.name}
              switchFundName={data?.data?.switchSuggestions?.[0]?.name}
              highExposureFundPercentage={data?.data?.highExposureThreshold}
              isHighExposureFund={data?.data?.isHighExposureFund}
              reasonsToSwitch={data?.data?.switchSuggestions?.[0]?.reasonsToSwitch}
              powerRank={data?.data?.switchSuggestions?.[0]?.powerRank}
              missedGains={data?.data?.missedGains}
              currentWealth={data?.data?.currentWealth || 0}
              investedAmount={data?.data?.investedAmount || 0}
              missedGainsAsOfDate={data?.data?.missedGainsAsOfDate}
              recommendationDate={data?.data?.recommendationDate}
            />
          </div>
          {data?.data?.taxDetails && data?.data?.rebalanceStrategyType !== RebalanceType.SIP && (
            <div className='mt-3'>
              <ExitLoadAndTax
                isElss={data?.data?.isElss}
                isHighExposureFund={data?.data?.isHighExposureFund}
                rebalanceStrategyType={data?.data?.rebalanceStrategyType}
                {...data?.data?.taxDetails}
                highExposureTransferAmount={data?.data?.highExposureTransferAmount}
                currentWealth={data?.data?.currentWealth}
              />
            </div>
          )}
          {!data?.data?.recommendationDate && (
            <div className='bg-grey80 mt-3'>
              <FAQRebalancing faqHeader='FAQ' />
            </div>
          )}
          <RebalancingDisclaimer />
        </div>
      )}
    </div>
  )
}

const CloseIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
  <circle cx="12" cy="12.5" r="12" fill="#FFFFFF"/>
  <path d="M16 8.5L8 16.5" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M8 8.5L16 16.5" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`
